/** Presentation of the sim's committed leap. No destination/outcome decisions. */
import type { Observation, ObservedEnemy } from "@pd/shared";
import { dirForDelta } from "./hero.js";
import { enemyKey } from "./interpolate.js";
import type { InterpState } from "./interpolate.js";
import type { Vec2 } from "./actions.js";

export function pounceVisual(enemy: ObservedEnemy, tick: number, fraction = 0):
  { pos: Vec2; frame: number; windup?: number } | null {
  const p = enemy.pounce;
  if (!enemy.alive || !p || tick < p.startTick || tick >= p.endTick) return null;
  if (tick < p.launchTick) return { pos: {x:p.fromX,y:p.fromY,height:0}, frame:0,
    windup:(tick+fraction-p.startTick)/(p.launchTick-p.startTick) };
  if (tick < p.landTick) {
    // Clamp to the observed phase: cosmetic sub-tick smoothing never runs
    // ahead into landing damage/recovery before the authoritative packet.
    const t = Math.min(1,Math.max(0,(tick+fraction-p.launchTick)/(p.landTick-p.launchTick)));
    return {pos:{x:p.fromX+(p.toX-p.fromX)*t,y:p.fromY+(p.toY-p.fromY)*t,
      height:32*4*t*(1-t)},frame:1};
  }
  return {pos:{x:enemy.x,y:enemy.y,height:0},frame:2};
}

export function applyPounceVisuals(interp: InterpState, positions: Map<string, Vec2>, obs: Observation,
  tick: number, fraction = 0): void {
  for (const e of obs.enemies) {
    const visual=pounceVisual(e,tick,fraction);
    if (!visual) continue;
    const key=enemyKey(e.id); const anim=interp.anims.get(key);
    if (!anim) continue;
    const p=e.pounce!;
    const dir=dirForDelta(p.toX-p.fromX,p.toY-p.fromY);
    if(dir) anim.dir=dir;
    anim.pounceFrame=visual.frame; anim.pounceHeight=visual.pos.height;
    anim.pounceWindup=visual.windup; anim.walkDist=0;
    anim.pounceElement=p.element;
    positions.set(key,visual.pos);
    // Keep ordinary walking ready to resume from the actual landing, with
    // no stale pull/step interpolation or backslide after recovery clears.
    interp.steps.delete(key); interp.lastLerped.set(key,visual.pos);
  }
}
